import {useEffect, useRef, useState} from 'react';
import { useAuth, useLocale } from '../../context';
import { api, formatCashierApiError, isCashierConnectionError, isRealActiveCashierLock } from '../../api';
import { mergePaymentMethods, groupPaymentMethods } from '../../data/paymentMethods';
import { formatMoney, formatUsd, formatUsdWithTry } from '../../utils/currency';
import RequestsEmptyState from '../account/RequestsEmptyState';
import { ActiveCryptoPanel, CryptoAssetPicker } from '../account/CryptoCashierFields';
import CashierSoftBoundary from '../account/CashierSoftBoundary';
import {
  ActivePayurusPanel,
  PayurusReturnPanel,
  parsePayurusReturnParams,
  clearPayurusReturnParams,
  setCashierReturnParams,
} from '../account/ActivePayurusPanel';
import { ActiveHavaleSpeedPanel } from '../account/ActiveHavaleSpeedPanel';
import { ActiveMaxHavalePanel, openMaxHavalePaymentUrl } from '../account/ActiveMaxHavalePanel';
import { ActiveHavalePanel, openHavalePaymentUrl } from '../account/ActiveHavalePanel';
import {
  ensureRealtimeConnected,
  isRealtimeConnected,
  setRealtimeAuthToken,
  subscribeRealtimeStatus,
  subscribeRealtimeTopic,
} from '../../utils/realtimeSocket';

function IconChevronDown() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 8" fill="none" aria-hidden="true">
      <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconWarning() {
  return (
    <svg width="40" height="40" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M1.6665 10.5002C1.6665 5.89836 5.39799 2.16687 9.99984 2.16687C14.6022 2.16687 18.3332 5.89808 18.3332 10.5002C18.3332 15.1023 14.6022 18.8335 9.99984 18.8335C5.39799 18.8335 1.6665 15.102 1.6665 10.5002ZM9.1665 6.33354C9.1665 5.8733 9.5396 5.5002 9.99984 5.5002C10.4601 5.5002 10.8332 5.8733 10.8332 6.33354V10.5002C10.8332 10.9604 10.4601 11.3335 9.99984 11.3335C9.5396 11.3335 9.1665 10.9604 9.1665 10.5002V6.33354ZM9.99984 14.6669C9.5396 14.6669 9.1665 14.2938 9.1665 13.8335C9.1665 13.3733 9.5396 13.0002 9.99984 13.0002C10.4601 13.0002 10.8332 13.3733 10.8332 13.8335C10.8332 14.2938 10.4601 14.6669 9.99984 14.6669Z"
        fill="currentColor"
      />
    </svg>
  );
}

function MethodIcon({ type, imageUrl, name }) {
  const fallback = type === 'bank' || type === 'transfer' || type === 'havale'
    ? '/payment-icons/bank.svg'
    : '/payment-icons/crypto.svg';
  const initial = imageUrl && imageUrl !== fallback ? imageUrl : fallback;
  const [src, setSrc] = useState(initial);
  useEffect(() => {
    setSrc(imageUrl && imageUrl !== fallback ? imageUrl : fallback);
  }, [imageUrl, fallback]);
  return (
    <span className={`mobile-account-cashier__method-icon mobile-account-cashier__method-icon--${type === 'bank' || type === 'havale' ? 'bank' : 'crypto'}`}>
      <img
        src={src}
        alt={name || ''}
        width="40"
        height="40"
        onError={() => {
          if (src !== fallback) setSrc(fallback);
        }}
      />
    </span>
  );
}

function DepositMethodList({methods, onSelect, tr: translateProp, t: translateLegacy}) {
  const tr = typeof translateProp === 'function' ? translateProp : (typeof translateLegacy === 'function' ? translateLegacy : (key) => key);
  if (!methods.length) {
    return (
      <div className="mobile-account-cashier__methods-empty">
        <p>{tr('account.payments.noMethods')}</p>
      </div>
    );
  }
  const groups = groupPaymentMethods(methods);
  return (
    <div className="mobile-account-cashier__method-groups">
      {groups.map((group) => (
        <section key={group.id} className="mobile-account-cashier__method-group" aria-label={tr(group.nameKey)}>
          <h3 className="mobile-account-cashier__method-group-title">{tr(group.nameKey)}</h3>
          <div className="mobile-account-cashier__method-cards" role="list">
            {group.methods.map((item) => {
              const name = item.name || tr(item.nameKey);
              const iconType = item.icon || (item.channel === 'crypto' || item.id === 'direct_crypto' ? 'crypto' : 'bank');
              return (
                <button
                  key={item.id}
                  type="button"
                  role="listitem"
                  className="mobile-account-cashier__method-card"
                  onClick={() => onSelect(item.id)}
                >
                  <MethodIcon type={iconType} imageUrl={item.imageUrl} name={name} />
                  <span className="mobile-account-cashier__method-card-info">
                    <span className="mobile-account-cashier__method-card-name">{name}</span>
                    <span className="mobile-account-cashier__method-card-min">
                      {(item.currency === 'USD' || item.channel === 'crypto' || item.id === 'direct_crypto')
                        ? tr('account.payments.fromUsd', { amount: formatUsd(item.min) })
                        : tr('account.payments.from', { amount: item.min })}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

function formatRequestDate(value) {
  const date = new Date(value);
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function isOpenCashierRequest(req) {
  if (!req) return false;
  const st = String(req.status || '').toLowerCase();
  return st === 'pending' || st === 'processing' || !st;
}

function hasCashierTransferDetails(req) {
  return Boolean(
    req?.iban
    || req?.redirect_url
    || req?.address
    || req?.account_name
    || req?.bank_name
    || req?.wallet_id,
  );
}

function mergeCashierActiveRequest(prev, next) {
  if (!next) return null;
  if (prev?.id === next.id) {
    const depositConfirmed = Boolean(next.deposit_confirmed || prev.deposit_confirmed || next.user_confirmed || prev.user_confirmed);
    const confirmStatus = (next.confirm_status === 'notified' || prev.confirm_status === 'notified')
      ? 'notified'
      : (next.confirm_status || prev.confirm_status || null);
    return {
      ...prev,
      ...next,
      iban: next.iban || prev.iban || null,
      account_name: next.account_name || prev.account_name || null,
      bank_name: next.bank_name || prev.bank_name || null,
      redirect_url: next.redirect_url || prev.redirect_url || null,
      address: next.address || prev.address || null,
      wallet_id: next.wallet_id || prev.wallet_id || null,
      deposit_confirmed: depositConfirmed,
      user_confirmed: depositConfirmed,
      confirm_status: confirmStatus,
    };
  }
  return next;
}

function applyCashierActiveRequest(setter, next, { allowClear = true } = {}) {
  setter((prev) => {
    if (next) return mergeCashierActiveRequest(prev, next);
    if (allowClear === 'force') return null;
    if (prev && isOpenCashierRequest(prev) && hasCashierTransferDetails(prev)) return prev;
    if (allowClear === false) return prev;
    return null;
  });
}

function isTerminalCashierReturn(params) {
  if (!params) return false;
  if (params.hintedSuccess || params.hintedFail) return true;
  const flag = String(params.flag || '').toLowerCase();
  return flag === 'success' || flag === 'failed' || flag === 'fail' || flag === 'cancelled' || flag === 'expired';
}

function formatCashierTxStatus(tr, tx) {
  const status = String(tx?.status || 'completed').toLowerCase();
  const provider = String(tx?.payment_provider || tx?.payment_method || '').toLowerCase();
  if (provider === 'havalespeed') {
    if (status === 'completed' || status === 'approved') return tr('account.payments.status_approved');
    if (status === 'rejected' || status === 'failed') return tr('account.payments.status_rejected');
    if (status === 'cancelled') {
      const reason = tx?.payment_details?.cancel_reason;
      if (reason === 'cancelled_by_user') return tr('account.payments.status_cancelled');
      return tr('account.payments.status_rejected');
    }
  }
  return tr(`account.payments.status_${status}`);
}

function RequestsPanel({transactions, tr: translateProp, t: translateLegacy}) {
  const tr = typeof translateProp === 'function' ? translateProp : (typeof translateLegacy === 'function' ? translateLegacy : (key) => key);
  if (!transactions.length) {
    return <RequestsEmptyState messageKey="account.payments.requestsEmpty" />;
  }

  return (
    <div className="mobile-account-cashier__requests">
      {transactions.map((tx) => (
          <div key={tx.id} className="mobile-account-cashier__request-row">
            <div className="mobile-account-cashier__request-main">
              <span className="mobile-account-cashier__request-type">
                {tx.type === 'deposit' ? tr('account.cashier.deposit') : tr('account.cashier.withdraw')}
              </span>
              <span className="mobile-account-cashier__request-date">{formatRequestDate(tx.created_at)}</span>
            </div>
            <div className="mobile-account-cashier__request-meta">
              <span>
                {tx.type === 'deposit'
                  && (tx.payment_provider === 'direct_crypto' || tx.payment_channel === 'crypto')
                  && tx.payment_details?.amount_usd != null
                  ? formatUsdWithTry(tx.payment_details.amount_usd, tx.payment_details.rate_usd_try)
                  : formatMoney(tx.amount)}
              </span>
              <span>{formatCashierTxStatus(tr, tx)}</span>
            </div>
          </div>
      ))}
    </div>
  );
}

export default function MobileAccountCashier({
  summary,
  initialTab = 'deposit',
  selectedMethodId = null,
  onSelectMethod,
  onClearMethod,
  onUpdated,
  onDeposit,
}) {
  const _locale = useLocale();
  const tr = _locale.t;
  const { token, setSession, user } = useAuth();
  const depositMethodFirst = initialTab === 'deposit';
  const [tab, setTab] = useState(initialTab === 'deposit' ? 'deposit' : 'withdraw');
  const [depositMethods, setDepositMethods] = useState([]);
  const [withdrawMethods, setWithdrawMethods] = useState([]);
  const [methodId, setMethodId] = useState(selectedMethodId || null);
  const [amount, setAmount] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [activeRequest, setActiveRequest] = useState(null);
  const [cryptoAssets, setCryptoAssets] = useState([]);
  const [cryptoLoadKey, setCryptoLoadKey] = useState('');
  const [cryptoAssetId, setCryptoAssetId] = useState(null);
  const [cryptoStep, setCryptoStep] = useState('coin');
  const [payurusReturn, setPayurusReturn] = useState(() => parsePayurusReturnParams());
  const [payurusReturnPhase, setPayurusReturnPhase] = useState(() => {
    const parsed = parsePayurusReturnParams();
    return isTerminalCashierReturn(parsed) ? 'checking' : null;
  });
  const [payurusReturnRequest, setPayurusReturnRequest] = useState(null);
  const [returnProvider, setReturnProvider] = useState(null);
  const [walletAddress, setWalletAddress] = useState('');
  const [walletMemo, setWalletMemo] = useState('');
  const [err, setErr] = useState('');
  const [errDetail, setErrDetail] = useState('');
  const [loading, setLoading] = useState(false);
  const [cancellingPayurus, setCancellingPayurus] = useState(false);
  const [confirmingHavaleSpeed, setConfirmingHavaleSpeed] = useState(false);
  const [cancellingHavaleSpeed, setCancellingHavaleSpeed] = useState(false);
  const [cancellingMaxHavale, setCancellingMaxHavale] = useState(false);
  const [cancellingHavale, setCancellingHavale] = useState(false);
  const [methodsLoading, setMethodsLoading] = useState(true);
  const [methodOpen, setMethodOpen] = useState(false);

  const methods = tab === 'deposit' ? depositMethods : withdrawMethods;
  const method = methods.find((item) => item.id === methodId) || methods[0] || null;
  const balance = Number(summary?.balance || 0);
  const methodMin = Number(method?.min || 50);
  const methodMax = method?.max != null ? Number(method.max) : 100000;
  const insufficient = tab === 'withdraw' && Boolean(method) && balance < methodMin;
  const singleMethod = methods.length <= 1;
  const isCrypto = methodId === 'direct_crypto';
  const isPayurus = methodId === 'payurus';
  const isPayferrari = methodId === 'payferrari';
  const isHavaleSpeed = methodId === 'havalespeed';
  const isMaxHavale = methodId === 'maxhavale';
  const isBtqHavale = methodId === 'havale';
  const isHavaleDeposit = isHavaleSpeed || isMaxHavale || isBtqHavale;
  const cryptoFetchKey = token && isCrypto ? `${tab}:direct_crypto` : '';
  const cryptoAssetsLoading = Boolean(cryptoFetchKey) && cryptoLoadKey !== cryptoFetchKey;
  const showCryptoAmount = !isCrypto || (isCrypto && tab === 'withdraw' && cryptoStep === 'wallet');
  const showActive = !(
      payurusReturnPhase
      && !(activeRequest && isOpenCashierRequest(activeRequest) && hasCashierTransferDetails(activeRequest))
    )
    && (
      (isCrypto && activeRequest?.provider === 'direct_crypto')
      || (isPayurus && activeRequest?.provider === 'payurus') || (isPayferrari && activeRequest?.provider === 'payferrari')
      || (isHavaleSpeed && activeRequest?.provider === 'havalespeed')
      || (isMaxHavale && activeRequest?.provider === 'maxhavale')
      || (isBtqHavale && activeRequest?.provider === 'havale')
    )
    && activeRequest
    && activeRequest.type === tab;
  const quick = (method?.quickAmounts || []).filter((item) => item >= methodMin && item <= methodMax);
  const showDepositMethodPicker = depositMethodFirst
    && tab === 'deposit'
    && !selectedMethodId
    && !payurusReturnPhase;

  const txStatusSigRef = useRef('');
  const activeStatusRef = useRef('');
  const depositRetryRef = useRef(0);
  const depositFormRef = useRef(null);

  const loadTransactions = () => {
    if (!token) return;
    api.transactions(token).then(setTransactions).catch(() => {});
  };

  const loadActive = ({ allowClear = true } = {}) => {
    if (!token || (!isCrypto && !isPayurus && !isPayferrari && !isHavaleSpeed && !isMaxHavale && !isBtqHavale) || (tab !== 'deposit' && tab !== 'withdraw')) {
      setActiveRequest(null);
      return;
    }
    api.activeCashierRequest(token, { provider: methodId, type: tab })
      .then((data) => applyCashierActiveRequest(setActiveRequest, data.request || null, { allowClear }))
      .catch(() => applyCashierActiveRequest(setActiveRequest, null, { allowClear: false }));
  };

  useEffect(() => {
    setMethodsLoading(true);
    api.paymentMethods(token)
      .then((data) => {
        const merged = mergePaymentMethods(data.methods || []);
        setDepositMethods(merged.deposit);
        setWithdrawMethods(merged.withdraw);
        setMethodId((prev) => {
          if (selectedMethodId && (initialTab === 'deposit' ? merged.deposit : merged.withdraw).some((m) => m.id === selectedMethodId)) {
            return selectedMethodId;
          }
          const list = (initialTab === 'deposit' ? merged.deposit : merged.withdraw);
          return list.some((m) => m.id === prev) ? prev : (list[0]?.id || null);
        });
      })
      .catch(() => {
        setDepositMethods([]);
        setWithdrawMethods([]);
        setMethodId(selectedMethodId || null);
      })
      .finally(() => setMethodsLoading(false));
  }, [token, initialTab, selectedMethodId]);

  useEffect(() => {
    setTab(initialTab === 'deposit' ? 'deposit' : 'withdraw');
    setErr('');
    setErrDetail('');
  }, [initialTab]);

  useEffect(() => {
    if (selectedMethodId) {
      setMethodId(selectedMethodId);
      setMethodOpen(false);
    }
  }, [selectedMethodId]);

  useEffect(() => {
    if (tab === 'deposit' || tab === 'withdraw') {
      setAmount('');
      setErr('');
      setErrDetail('');
      loadActive();
    }
  }, [tab, methodId, token]);

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    let timer = null;

    const hasOpenItems = (list, request) => {
      const openTx = (Array.isArray(list) ? list : []).some((tx) => {
        const st = String(tx.status || '').toLowerCase();
        return st === 'pending' || st === 'processing';
      });
      const reqSt = String(request?.status || '').toLowerCase();
      const openReq = Boolean(request) && (reqSt === 'pending' || reqSt === 'processing' || !reqSt);
      return openTx || openReq;
    };

    const refresh = async () => {
      if (cancelled || !token) return;
      let nextList = null;
      let nextActive = activeRequest;
      try {
        nextList = await api.transactions(token);
        if (cancelled) return;
        setTransactions(nextList);
        const sig = JSON.stringify((Array.isArray(nextList) ? nextList : []).map((tx) => [tx.id, tx.status]));
        if (txStatusSigRef.current && txStatusSigRef.current !== sig) {
          onUpdated?.();
        }
        txStatusSigRef.current = sig;
      } catch {
      }

      if (!cancelled && (isCrypto || isPayurus || isHavaleSpeed || isMaxHavale || isBtqHavale) && (tab === 'deposit' || tab === 'withdraw')) {
        try {
          const data = await api.activeCashierRequest(token, { provider: methodId, type: tab });
          if (cancelled) return;
          nextActive = data.request || null;
          const activeSig = nextActive
            ? `${nextActive.id}:${nextActive.status}:${nextActive.confirmations || 0}:${nextActive.tx_hash || ''}`
            : '';
          if (activeStatusRef.current && activeStatusRef.current !== activeSig) {
            const st = String(nextActive?.status || '').toLowerCase();
            if (!nextActive || ['completed', 'approved', 'cancelled', 'rejected', 'failed', 'expired'].includes(st)) {
              onUpdated?.();
            }
          }
          activeStatusRef.current = activeSig;
          if (nextActive) {
            applyCashierActiveRequest(setActiveRequest, nextActive);
          } else if (Array.isArray(nextList)) {
            setActiveRequest((prev) => {
              if (!prev?.id) return null;
              const mine = nextList.find((tx) => Number(tx.id) === Number(prev.id));
              if (mine) {
                const st = String(mine.status || '').toLowerCase();
                if (['completed', 'approved', 'cancelled', 'rejected', 'failed', 'expired'].includes(st)) {
                  return mergeCashierActiveRequest(prev, {
                    ...prev,
                    status: mine.status,
                    amount: mine.amount != null ? mine.amount : prev.amount,
                  });
                }
              }
              if (isOpenCashierRequest(prev) && hasCashierTransferDetails(prev)) return prev;
              return null;
            });
          } else {
            applyCashierActiveRequest(setActiveRequest, nextActive);
          }
        } catch {
        }
      }

      if (cancelled) return;
      const open = hasOpenItems(nextList ?? transactions, nextActive);
      const base = open ? 2500 : 5000;
      const interval = isRealtimeConnected() ? (open ? 12000 : 30000) : base;
      timer = window.setTimeout(refresh, interval);
    };

    setRealtimeAuthToken(token);
    ensureRealtimeConnected();
    const unsub = subscribeRealtimeTopic('cashier', (data) => {
      if (cancelled || !data) return;
      if (Array.isArray(data.transactions)) {
        setTransactions(data.transactions);
        const sig = JSON.stringify(data.transactions.map((tx) => [tx.id, tx.status]));
        if (txStatusSigRef.current && txStatusSigRef.current !== sig) onUpdated?.();
        txStatusSigRef.current = sig;
      }
      if (data.activeRequest) {
        const nextActive = data.activeRequest;
        const activeSig = `${nextActive.id}:${nextActive.status}:${nextActive.confirmations || 0}:${nextActive.tx_hash || ''}`;
        if (activeStatusRef.current && activeStatusRef.current !== activeSig) {
          const st = String(nextActive?.status || '').toLowerCase();
          if (['completed', 'approved', 'cancelled', 'rejected', 'failed', 'expired'].includes(st)) {
            onUpdated?.();
          }
        }
        activeStatusRef.current = activeSig;
        applyCashierActiveRequest(setActiveRequest, nextActive);
      } else if (Object.prototype.hasOwnProperty.call(data, 'activeRequest') && Array.isArray(data.transactions)) {
        setActiveRequest((prev) => {
          if (!prev?.id) return prev;
          const mine = data.transactions.find((tx) => Number(tx.id) === Number(prev.id));
          if (!mine) return prev;
          const st = String(mine.status || '').toLowerCase();
          if (!['completed', 'approved', 'cancelled', 'rejected', 'failed', 'expired'].includes(st)) {
            return prev;
          }
          return mergeCashierActiveRequest(prev, {
            ...prev,
            status: mine.status,
            amount: mine.amount != null ? mine.amount : prev.amount,
          });
        });
      }
    });
    const unsubStatus = subscribeRealtimeStatus(() => {});

    refresh();
    return () => {
      cancelled = true;
      unsub();
      unsubStatus();
      if (timer) window.clearTimeout(timer);
    };
  }, [token, tab, methodId, isCrypto, isPayurus, isHavaleSpeed, isMaxHavale, isBtqHavale]);

  useEffect(() => {
    if (!payurusReturn) return;
    setTab('deposit');
    const provider = payurusReturn.provider || 'payurus';
    setMethodId(provider);
    onSelectMethod?.(provider);
  }, [payurusReturn]);

  useEffect(() => {
    if (!payurusReturn || !token) return undefined;
    if (!isTerminalCashierReturn(payurusReturn)) {
      clearPayurusReturnParams();
      setPayurusReturn(null);
      setPayurusReturnPhase(null);
      loadActive({ allowClear: false });
      return undefined;
    }
    let cancelled = false;
    let tries = 0;
    let timer = null;
    const provider = payurusReturn.provider || 'payurus';

    const finish = (phase, req = null) => {
      if (cancelled) return;
      if (req) setPayurusReturnRequest(req);
      setPayurusReturnPhase(phase);
      if (phase === 'completed' || phase === 'failed' || phase === 'expired') {
        clearPayurusReturnParams();
        if (phase === 'completed') {
          onUpdated?.();
          loadTransactions();
        }
      }
    };

    const poll = async () => {
      try {
        let req = null;
        if (payurusReturn.requestId) {
          const data = await api.cashierRequest(token, payurusReturn.requestId);
          req = data.request || null;
        } else {
          const data = await api.activeCashierRequest(token, { provider, type: 'deposit' });
          req = data.request || null;
        }
        if (cancelled) return;
        if (req && isOpenCashierRequest(req)) {
          applyCashierActiveRequest(setActiveRequest, req, { allowClear: false });
          clearPayurusReturnParams();
          setPayurusReturn(null);
          setPayurusReturnPhase(null);
          setPayurusReturnRequest(null);
          return;
        }
        if (req) setPayurusReturnRequest(req);
        const st = String(req?.status || '').toLowerCase();
        if (st === 'completed' || st === 'approved') {
          finish('completed', req);
          return;
        }
        if (st === 'cancelled' || st === 'rejected' || st === 'failed') {
          finish('failed', req);
          return;
        }
        if (st === 'expired') {
          finish('expired', req);
          return;
        }
        if (!req && payurusReturn.hintedSuccess) {
          finish('completed', null);
          return;
        }
        if (!req && payurusReturn.hintedFail) {
          finish('failed', null);
          return;
        }
        setPayurusReturnPhase(req ? 'pending' : 'checking');
        tries += 1;
        if (tries < 45) {
          timer = window.setTimeout(poll, 2000);
        } else if (payurusReturn.hintedSuccess) {
          finish('completed', req);
        } else {
          setPayurusReturnPhase('pending');
        }
      } catch {
        if (cancelled) return;
        tries += 1;
        if (payurusReturn.hintedSuccess && tries >= 3) {
          finish('completed', null);
          return;
        }
        if (tries < 45) timer = window.setTimeout(poll, 2000);
        else setPayurusReturnPhase(payurusReturn.hintedFail ? 'failed' : 'pending');
      }
    };

    setPayurusReturnPhase((prev) => prev || 'checking');
    poll();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [payurusReturn, token]);

  const dismissPayurusReturn = () => {
    clearPayurusReturnParams();
    setPayurusReturn(null);
    setPayurusReturnPhase(null);
    setPayurusReturnRequest(null);
    setReturnProvider(null);
    setActiveRequest(null);
    loadActive();
    onUpdated?.();
    loadTransactions();
  };

  const showProviderReturn = (phase, req = null, provider = 'havalespeed') => {
    const providerKey = String(provider || methodId || 'payurus').toLowerCase();
    if (req) setPayurusReturnRequest(req);
    setReturnProvider(providerKey);
    setPayurusReturnPhase(phase);
    setActiveRequest(null);
    if (phase === 'completed' || phase === 'failed' || phase === 'expired') {
      clearPayurusReturnParams();
    } else if (req?.id) {
      setCashierReturnParams(providerKey, { requestId: req.id, flag: 'return' });
    }
    if (phase === 'completed') {
      onUpdated?.();
      loadTransactions();
    }
  };

  useEffect(() => {
    if (!token || !isCrypto) {
      setCryptoAssets([]);
      setCryptoAssetId(null);
      setCryptoLoadKey('');
      return;
    }
    let cancelled = false;
    api.cryptoAssets(token, tab === 'withdraw' ? 'withdraw' : 'deposit')
      .then((data) => {
        if (cancelled) return;
        const list = data.assets || [];
        setCryptoAssets(list);
        setCryptoAssetId(list[0]?.id || null);
        setCryptoLoadKey(`${tab}:direct_crypto`);
      })
      .catch(() => {
        if (cancelled) return;
        setCryptoAssets([]);
        setCryptoAssetId(null);
        setCryptoLoadKey(`${tab}:direct_crypto`);
      });
    return () => { cancelled = true; };
  }, [token, isCrypto, tab]);

  const submit = async (event) => {
    event?.preventDefault?.();
    setLoading(true);
    if (!depositRetryRef.current) {
      setErr('');
      setErrDetail('');
    }
    let retrySubmit = false;
    try {
      const value = Number(amount);
      const formFromEvent = event?.currentTarget ? new FormData(event.currentTarget) : (depositFormRef.current || new FormData());
      if (event?.currentTarget) depositFormRef.current = formFromEvent;
      let res;
      if (methodId === 'direct_crypto') {
        if (tab === 'deposit') {
          res = await api.cryptoDeposit(token, {
            amount: value,
            assetId: cryptoAssetId,
          });
          if (res.request) setActiveRequest(res.request);
        } else {
          res = await api.cryptoWithdraw(token, {
            amount: value,
            assetId: cryptoAssetId,
            address: walletAddress,
            memo: walletMemo,
          });
          if (res.request) setActiveRequest(res.request);
          if (res.user) setSession(token, res.user);
        }
        onUpdated?.();
        loadTransactions();
      } else if (methodId === 'payferrari') {
        const form = formFromEvent;
        if (tab === 'deposit') {
          res = await api.payferrariDeposit(token, {
            amount: value,
            redirectUrl: `${window.location.origin}/account/cashier?tab=deposit&provider=payferrari`,
          });
          if (res.request) applyCashierActiveRequest(setActiveRequest, res.request, { allowClear: false });
          if (res.redirectUrl) {
            window.location.assign(res.redirectUrl);
            return;
          }
        } else {
          res = await api.payferrariWithdraw(token, {
            amount: value,
            walletId: String(form.get('walletId') || '').trim(),
            walletName: String(form.get('walletName') || '').trim(),
          });
          if (res.request) applyCashierActiveRequest(setActiveRequest, res.request, { allowClear: false });
          if (res.user) setSession(token, res.user);
        }
        onUpdated?.();
        loadTransactions();
      } else if (methodId === 'payurus') {
        const form = formFromEvent;
        if (tab === 'deposit') {
          res = await api.payurusDeposit(token, {
            amount: value,
            redirectUrl: `${window.location.origin}/account/cashier?tab=deposit&provider=payurus`,
          });
          if (res.request) applyCashierActiveRequest(setActiveRequest, res.request, { allowClear: false });
          if (res.redirectUrl) {
            window.location.assign(res.redirectUrl);
            return;
          }
        } else {
          res = await api.payurusWithdraw(token, {
            amount: value,
            walletId: String(form.get('walletId') || '').trim(),
            walletName: String(form.get('walletName') || '').trim(),
          });
          if (res.request) applyCashierActiveRequest(setActiveRequest, res.request, { allowClear: false });
          if (res.user) setSession(token, res.user);
        }
        onUpdated?.();
        loadTransactions();
      } else if (methodId === 'havalespeed') {
        const form = formFromEvent;
        if (tab === 'deposit') {
          const accountHolder = String(form.get('accountHolder') || '').trim();
          if (!accountHolder || accountHolder.length < 3) {
            throw new Error(tr('account.payments.havalespeed.accountHolder'));
          }
          res = await api.havaleSpeedDeposit(token, { amount: value, accountHolder, fullName: accountHolder });
          if (res.request) {
            applyCashierActiveRequest(setActiveRequest, res.request, { allowClear: false });
          }
        } else {
          res = await api.havaleSpeedWithdraw(token, {
            amount: value,
            iban: String(form.get('iban') || '').trim(),
            accountHolder: String(form.get('accountHolder') || '').trim(),
          });
          if (res.request) applyCashierActiveRequest(setActiveRequest, res.request, { allowClear: false });
          if (res.user) setSession(token, res.user);
        }
        onUpdated?.();
        loadTransactions();
      } else if (methodId === 'maxhavale') {
        const form = formFromEvent;
        if (tab === 'deposit') {
          const accountHolder = String(form.get('accountHolder') || '').trim();
          if (!accountHolder || accountHolder.length < 3) {
            throw new Error(tr('account.payments.havalespeed.accountHolder'));
          }
          res = await api.maxHavaleDeposit(token, {
            amount: value,
            accountHolder,
            fullName: accountHolder,
            redirectUrl: `${window.location.origin}/account/cashier?tab=deposit&provider=maxhavale`,
          });
          if (res.request) applyCashierActiveRequest(setActiveRequest, res.request, { allowClear: false });
          if (res.redirectUrl) openMaxHavalePaymentUrl(res.redirectUrl);
        } else {
          res = await api.maxHavaleWithdraw(token, {
            amount: value,
            bankName: String(form.get('bankName') || '').trim(),
            iban: String(form.get('iban') || '').trim(),
            accountHolder: String(form.get('accountHolder') || '').trim(),
          });
          if (res.request) applyCashierActiveRequest(setActiveRequest, res.request, { allowClear: false });
          if (res.user) setSession(token, res.user);
        }
        onUpdated?.();
        loadTransactions();
      } else if (methodId === 'havale') {
        const form = formFromEvent;
        if (tab === 'deposit') {
          const accountHolder = String(form.get('accountHolder') || '').trim();
          if (!accountHolder || accountHolder.length < 3) {
            throw new Error(tr('account.payments.havalespeed.accountHolder'));
          }
          res = await api.havaleDeposit(token, {
            amount: value,
            accountHolder,
            fullName: accountHolder,
            redirectUrl: `${window.location.origin}/account/cashier?tab=deposit&provider=havale`,
          });
          if (res.redirectUrl) {
            openHavalePaymentUrl(res.redirectUrl);
            return;
          }
          if (res.request) applyCashierActiveRequest(setActiveRequest, res.request, { allowClear: false });
        } else {
          res = await api.havaleWithdraw(token, {
            amount: value,
            bankName: String(form.get('bankName') || '').trim(),
            iban: String(form.get('iban') || '').trim(),
            accountHolder: String(form.get('accountHolder') || '').trim(),
          });
          if (res.request) applyCashierActiveRequest(setActiveRequest, res.request, { allowClear: false });
          if (res.user) setSession(token, res.user);
        }
        onUpdated?.();
        loadTransactions();
      } else {
        throw new Error('Ödeme yöntemi desteklenmiyor');
      }
      depositRetryRef.current = 0;
    } catch (error) {
      const formatted = formatCashierApiError(error, tr);
      const connectionErr = tab === 'deposit' && isCashierConnectionError(error, formatted);
      if (connectionErr) {
        // Bağlantı hatası: loadActive() çağırma (wait lock olmasın).
        setActiveRequest(null);
        if (depositRetryRef.current < 2) {
          depositRetryRef.current += 1;
          setErr('Bağlantı hatası. Tekrar deneniyor…');
          setErrDetail('');
          retrySubmit = true;
        } else {
          depositRetryRef.current = 0;
          setErr('Bağlantı hatası. Lütfen tekrar deneyin.');
          setErrDetail('');
        }
      } else {
        depositRetryRef.current = 0;
        setErr(formatted.title);
        setErrDetail(formatted.detail || '');
        if (isRealActiveCashierLock(error)) {
          loadActive();
        }
      }
    } finally {
      if (!retrySubmit) setLoading(false);
    }
    if (retrySubmit) {
      await new Promise((resolve) => window.setTimeout(resolve, 400));
      return submit(event);
    }
  };

  const cancelPayurusDeposit = async () => {
    if (!token || !activeRequest?.id || cancellingPayurus) return;
    setCancellingPayurus(true);
    setErr('');
    setErrDetail('');
    try {
      if (activeRequest?.provider === 'payferrari') {
        await api.payferrariCancel(token, { requestId: activeRequest.id });
      } else {
        await api.payurusCancel(token, { requestId: activeRequest.id });
      }
      setActiveRequest(null);
      onUpdated?.();
      loadTransactions();
    } catch (error) {
      const formatted = formatCashierApiError(error, tr);
      setErr(formatted.title);
      setErrDetail(formatted.detail || '');
      loadActive();
    } finally {
      setCancellingPayurus(false);
    }
  };

  const confirmHavaleSpeedDeposit = async () => {
    if (!token || !activeRequest?.id || confirmingHavaleSpeed) return false;
    setConfirmingHavaleSpeed(true);
    setErr('');
    setErrDetail('');
    try {
      const res = await api.havaleSpeedConfirm(token, { requestId: activeRequest.id });
      if (res.request) {
        applyCashierActiveRequest(setActiveRequest, {
          ...res.request,
          deposit_confirmed: true,
          user_confirmed: true,
          confirm_status: 'notified',
        }, { allowClear: false });
      } else {
        loadActive({ allowClear: false });
      }
      onUpdated?.();
      loadTransactions();
      return true;
    } catch (error) {
      const formatted = formatCashierApiError(error, tr);
      setErr(formatted.title);
      setErrDetail(formatted.detail || '');
      loadActive();
      return false;
    } finally {
      setConfirmingHavaleSpeed(false);
    }
  };

  const cancelHavaleSpeedDeposit = async () => {
    if (!token || !activeRequest?.id || cancellingHavaleSpeed) return;
    setCancellingHavaleSpeed(true);
    setErr('');
    setErrDetail('');
    try {
      const cancelled = activeRequest;
      await api.havaleSpeedCancel(token, { requestId: activeRequest.id });
      showProviderReturn('failed', cancelled, 'havalespeed');
      onUpdated?.();
      loadTransactions();
    } catch (error) {
      const formatted = formatCashierApiError(error, tr);
      setErr(formatted.title);
      setErrDetail(formatted.detail || '');
      loadActive();
    } finally {
      setCancellingHavaleSpeed(false);
    }
  };

  const cancelMaxHavaleDeposit = async () => {
    if (!token || !activeRequest?.id || cancellingMaxHavale) return;
    setCancellingMaxHavale(true);
    setErr('');
    setErrDetail('');
    try {
      const cancelled = activeRequest;
      await api.maxHavaleCancel(token, { requestId: activeRequest.id });
      showProviderReturn('failed', cancelled, 'maxhavale');
      onUpdated?.();
      loadTransactions();
    } catch (error) {
      const formatted = formatCashierApiError(error, tr);
      setErr(formatted.title);
      setErrDetail(formatted.detail || '');
      loadActive();
    } finally {
      setCancellingMaxHavale(false);
    }
  };

  const handleHavaleSpeedOutcome = (phase, req) => {
    showProviderReturn(phase, req || activeRequest, 'havalespeed');
  };

  const handleCryptoOutcome = (phase, req) => {
    showProviderReturn(phase, req || activeRequest, 'direct_crypto');
  };

  const handleMaxHavaleOutcome = (phase, req) => {
    showProviderReturn(phase, req || activeRequest, 'maxhavale');
  };

  const cancelHavaleDeposit = async () => {
    if (!token || !activeRequest?.id || cancellingHavale) return;
    setCancellingHavale(true);
    setErr('');
    setErrDetail('');
    try {
      const cancelled = activeRequest;
      await api.havaleCancel(token, { requestId: activeRequest.id });
      showProviderReturn('failed', cancelled, 'havale');
      onUpdated?.();
      loadTransactions();
    } catch (error) {
      const formatted = formatCashierApiError(error, tr);
      setErr(formatted.title);
      setErrDetail(formatted.detail || '');
      loadActive();
    } finally {
      setCancellingHavale(false);
    }
  };

  const handleHavaleOutcome = (phase, req) => {
    showProviderReturn(phase, req || activeRequest, 'havale');
  };

  const tabs = initialTab === 'deposit'
    ? [
      { id: 'deposit', label: tr('account.cashier.deposit') },
      { id: 'requests', label: tr('account.payments.requests') },
    ]
    : [
      { id: 'withdraw', label: tr('account.cashier.withdraw') },
      { id: 'requests', label: tr('account.payments.requests') },
    ];

  const nameParts = String(user?.name || '').trim().split(/\s+/).filter(Boolean);
  const defaultFirst = nameParts[0] || '';
  const defaultLast = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';

  const handleSelectDepositMethod = (id) => {
    setMethodId(id);
    setMethodOpen(false);
    if (onSelectMethod) onSelectMethod(id);
  };

  const handleClearMethod = () => {
    if (onClearMethod) onClearMethod();
    else setMethodOpen(true);
  };

  return (
    <div className="mobile-account-cashier">
      {!showDepositMethodPicker && (
        <div className="mobile-account-cashier__tabs" role="tablist">
          {tabs.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={tab === item.id}
              className={`mobile-account-cashier__tab${tab === item.id ? ' is-active' : ''}`}
              onClick={() => setTab(item.id)}
            >
              <span className="mobile-account-cashier__tab-label">{item.label}</span>
            </button>
          ))}
        </div>
      )}

      {tab === 'requests' ? (
        <RequestsPanel transactions={transactions} tr={tr} />
      ) : methodsLoading ? (
        <div className="mobile-account-cashier__panel mobile-account-cashier__panel--loading">
          <div className="mobile-account-cashier__loader" aria-hidden="true" />
          <p>{tr('account.loading')}</p>
        </div>
      ) : !methods.length ? (
        <div className="mobile-account-cashier__panel mobile-account-cashier__panel--loading">
          <p>{tr('account.payments.noMethods')}</p>
        </div>
      ) : showDepositMethodPicker ? (
        <div className="mobile-account-cashier__panel mobile-account-cashier__panel--methods">
          <DepositMethodList
            methods={depositMethods}
            onSelect={handleSelectDepositMethod}
            tr={tr}
          />
        </div>
      ) : !method ? (
        <div className="mobile-account-cashier__panel mobile-account-cashier__panel--loading">
          <p>{tr('account.payments.noMethods')}</p>
        </div>
      ) : (
        <div className="mobile-account-cashier__panel">
          {depositMethodFirst && selectedMethodId ? (
            <div className="mobile-account-cashier__selected-method">
              <MethodIcon
                type={method.icon || (method.channel === 'crypto' || method.id === 'direct_crypto' ? 'crypto' : 'bank')}
                imageUrl={method.imageUrl}
                name={method.name || tr(method.nameKey)}
              />
              <div className="mobile-account-cashier__selected-method-info">
                <span className="mobile-account-cashier__selected-method-name">
                  {method.name || tr(method.nameKey)}
                </span>
                <button
                  type="button"
                  className="mobile-account-cashier__change-method"
                  onClick={handleClearMethod}
                >
                  {tr('account.payments.selectAnotherMethod')}
                </button>
              </div>
            </div>
          ) : !singleMethod ? (
            <>
              <label className="mobile-account-cashier__field-label">
                {tr('account.payments.selectMethod')}
              </label>

              <div className="mobile-account-cashier__method-wrap">
                <button
                  type="button"
                  className={`mobile-account-cashier__method${methodOpen ? ' is-open' : ''}`}
                  onClick={() => setMethodOpen((value) => !value)}
                  aria-expanded={methodOpen}
                >
                  <span>{method.name || tr(method.nameKey)}</span>
                  <IconChevronDown />
                </button>
                {methodOpen && (
                  <div className="mobile-account-cashier__method-list">
                    {groupPaymentMethods(methods).map((group) => (
                      <div key={group.id} className="mobile-account-cashier__method-list-group">
                        <div className="mobile-account-cashier__method-list-group-title">{tr(group.nameKey)}</div>
                        {group.methods.map((item) => (
                          <button
                            key={item.id}
                            type="button"
                            className={`mobile-account-cashier__method-option${item.id === methodId ? ' is-active' : ''}`}
                            onClick={() => {
                              setMethodId(item.id);
                              setMethodOpen(false);
                            }}
                          >
                            {item.name || tr(item.nameKey)}
                          </button>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            <p className="mobile-account-cashier__field-label">{method.name || tr(method.nameKey)}</p>
          )}

          {payurusReturnPhase ? (
            <CashierSoftBoundary tr={tr} buttonClassName="mobile-account-cashier__notice-btn" onRetry={dismissPayurusReturn}>
              <PayurusReturnPanel
                phase={payurusReturnPhase}
                request={payurusReturnRequest}
                tr={tr}
                variant="mobile"
                provider={payurusReturn?.provider || returnProvider || methodId || method?.id || 'payurus'}
                onContinue={dismissPayurusReturn}
                onGoRequests={() => {
                  dismissPayurusReturn();
                  setTab('requests');
                }}
              />
            </CashierSoftBoundary>
          ) : showActive ? (
            <CashierSoftBoundary
              tr={tr}
              buttonClassName="mobile-account-cashier__notice-btn"
              onRetry={() => setActiveRequest(null)}
            >
            {isPayurus ? (
              <ActivePayurusPanel
                request={activeRequest}
                tr={tr}
                variant="mobile"
                onGoRequests={() => setTab('requests')}
                onCancel={tab === 'deposit' ? cancelPayurusDeposit : undefined}
                cancelling={cancellingPayurus}
              />
            ) : isHavaleSpeed ? (
              <ActiveHavaleSpeedPanel
                request={activeRequest}
                tr={tr}
                variant="mobile"
                token={token}
                onGoRequests={() => setTab('requests')}
                onConfirm={tab === 'deposit' ? confirmHavaleSpeedDeposit : undefined}
                confirming={confirmingHavaleSpeed}
                onCancel={tab === 'deposit' ? cancelHavaleSpeedDeposit : undefined}
                cancelling={cancellingHavaleSpeed}
                onOutcome={tab === 'deposit' ? handleHavaleSpeedOutcome : undefined}
                onContinue={dismissPayurusReturn}
              />
            ) : isMaxHavale ? (
              <ActiveMaxHavalePanel
                request={activeRequest}
                tr={tr}
                variant="mobile"
                token={token}
                onGoRequests={() => setTab('requests')}
                onCancel={tab === 'deposit' ? cancelMaxHavaleDeposit : undefined}
                cancelling={cancellingMaxHavale}
                onOpenPayment={openMaxHavalePaymentUrl}
                onOutcome={tab === 'deposit' ? handleMaxHavaleOutcome : undefined}
                onContinue={dismissPayurusReturn}
              />
            ) : isBtqHavale ? (
              <ActiveHavalePanel
                request={activeRequest}
                tr={tr}
                variant="mobile"
                token={token}
                onGoRequests={() => setTab('requests')}
                onCancel={tab === 'deposit' ? cancelHavaleDeposit : undefined}
                cancelling={cancellingHavale}
                onOpenPayment={openHavalePaymentUrl}
                onOutcome={tab === 'deposit' ? handleHavaleOutcome : undefined}
                onContinue={dismissPayurusReturn}
              />
            ) : (
              <ActiveCryptoPanel
                request={activeRequest}
                tr={tr}
                variant="mobile"
                token={token}
                onUpdated={(next) => {
                  if (next?.id) setActiveRequest(next);
                }}
                onOutcome={tab === 'deposit' ? handleCryptoOutcome : undefined}
                onContinue={dismissPayurusReturn}
                onGoRequests={() => setTab('requests')}
              />
            )}
            </CashierSoftBoundary>
          ) : insufficient ? (
            <div className="mobile-account-cashier__notice">
              <span className="mobile-account-cashier__notice-icon" aria-hidden="true">
                <IconWarning />
              </span>
              <span className="mobile-account-cashier__notice-title">
                {tr('account.payments.insufficientBalance')}
              </span>
              <p className="mobile-account-cashier__notice-text">
                {tr('account.payments.minWithdrawNotice', { amount: formatMoney(methodMin) })}
              </p>
              <div className="mobile-account-cashier__notice-actions">
                <button type="button" className="mobile-account-cashier__notice-btn is-primary" onClick={onDeposit}>
                  {tr('account.cashier.deposit')}
                </button>
                {!singleMethod ? (
                  <button
                    type="button"
                    className="mobile-account-cashier__notice-btn"
                    onClick={() => setMethodOpen(true)}
                  >
                    {tr('account.payments.selectAnotherMethod')}
                  </button>
                ) : null}
              </div>
            </div>
          ) : (
            <form className="mobile-account-cashier__form" onSubmit={submit}>
              
              {isHavaleDeposit && tab === 'deposit' && (
                <label className="mobile-account-cashier__field">
                  <span className="mobile-account-cashier__field-label">{tr('account.payments.havalespeed.accountHolder')}</span>
                  <input
                    className="mobile-account-cashier__input"
                    name="accountHolder"
                    required
                    minLength={3}
                    defaultValue={`${defaultFirst} ${defaultLast}`.trim()}
                    placeholder={tr('account.payments.havalespeed.accountHolder')}
                    autoComplete="name"
                  />
                </label>
              )}
              {(isPayurus || isPayferrari) && tab === 'withdraw' && (
                <>
                  <label className="mobile-account-cashier__field">
                    <span className="mobile-account-cashier__field-label">{tr(isPayferrari ? 'account.payments.payferrari.walletId' : 'account.payments.payurus.walletId')}</span>
                    <input
                      className="mobile-account-cashier__input"
                      name="walletId"
                      required
                      minLength={3}
                      placeholder={tr(isPayferrari ? 'account.payments.payferrari.walletId' : 'account.payments.payurus.walletId')}
                    />
                  </label>
                  <label className="mobile-account-cashier__field">
                    <span className="mobile-account-cashier__field-label">{tr(isPayferrari ? 'account.payments.payferrari.walletName' : 'account.payments.payurus.walletName')}</span>
                    <input
                      className="mobile-account-cashier__input"
                      name="walletName"
                      required
                      minLength={3}
                      defaultValue={`${defaultFirst} ${defaultLast}`.trim()}
                      placeholder={tr(isPayferrari ? 'account.payments.payferrari.walletName' : 'account.payments.payurus.walletName')}
                    />
                  </label>
                </>
              )}
              {isHavaleSpeed && tab === 'withdraw' && (
                <>
                  <label className="mobile-account-cashier__field">
                    <span className="mobile-account-cashier__field-label">{tr('account.payments.havale.iban')}</span>
                    <input
                      className="mobile-account-cashier__input"
                      name="iban"
                      required
                      placeholder={tr('account.payments.havale.ibanPlaceholder')}
                    />
                  </label>
                  <label className="mobile-account-cashier__field">
                    <span className="mobile-account-cashier__field-label">{tr('account.payments.havale.accountName')}</span>
                    <input
                      className="mobile-account-cashier__input"
                      name="accountHolder"
                      required
                      minLength={3}
                      defaultValue={`${defaultFirst} ${defaultLast}`.trim()}
                      placeholder={tr('account.payments.havale.accountName')}
                    />
                  </label>
                </>
              )}
              {(isMaxHavale) && tab === 'withdraw' && (
                <>
                  <label className="mobile-account-cashier__field">
                    <span className="mobile-account-cashier__field-label">{tr('account.payments.maxhavale.bankName')}</span>
                    <input
                      className="mobile-account-cashier__input"
                      name="bankName"
                      required
                      placeholder={tr('account.payments.maxhavale.bankName')}
                    />
                  </label>
                  <label className="mobile-account-cashier__field">
                    <span className="mobile-account-cashier__field-label">{tr('account.payments.havale.iban')}</span>
                    <input
                      className="mobile-account-cashier__input"
                      name="iban"
                      required
                      placeholder={tr('account.payments.havale.ibanPlaceholder')}
                    />
                  </label>
                  <label className="mobile-account-cashier__field">
                    <span className="mobile-account-cashier__field-label">{tr('account.payments.havale.accountName')}</span>
                    <input
                      className="mobile-account-cashier__input"
                      name="accountHolder"
                      required
                      minLength={3}
                      defaultValue={`${defaultFirst} ${defaultLast}`.trim()}
                      placeholder={tr('account.payments.havale.accountName')}
                    />
                  </label>
                </>
              )}
              {isBtqHavale && tab === 'withdraw' && (
                <>
                  <label className="mobile-account-cashier__field">
                    <span className="mobile-account-cashier__field-label">{tr('account.payments.havaleBtq.bankName')}</span>
                    <input
                      className="mobile-account-cashier__input"
                      name="bankName"
                      required
                      placeholder={tr('account.payments.havaleBtq.bankName')}
                    />
                  </label>
                  <label className="mobile-account-cashier__field">
                    <span className="mobile-account-cashier__field-label">{tr('account.payments.havale.iban')}</span>
                    <input
                      className="mobile-account-cashier__input"
                      name="iban"
                      required
                      placeholder={tr('account.payments.havale.ibanPlaceholder')}
                    />
                  </label>
                  <label className="mobile-account-cashier__field">
                    <span className="mobile-account-cashier__field-label">{tr('account.payments.havale.accountName')}</span>
                    <input
                      className="mobile-account-cashier__input"
                      name="accountHolder"
                      required
                      minLength={3}
                      defaultValue={`${defaultFirst} ${defaultLast}`.trim()}
                      placeholder={tr('account.payments.havale.accountName')}
                    />
                  </label>
                </>
              )}

              {isCrypto && (
                <CryptoAssetPicker
                  assets={cryptoAssets}
                  selectedId={cryptoAssetId}
                  onSelect={setCryptoAssetId}
                  tr={tr}
                  direction={tab === 'withdraw' ? 'withdraw' : 'deposit'}
                  walletAddress={walletAddress}
                  onWalletAddressChange={setWalletAddress}
                  walletMemo={walletMemo}
                  onWalletMemoChange={setWalletMemo}
                  loading={cryptoAssetsLoading}
                  onStepChange={setCryptoStep}
                  amount={amount}
                  onAmountChange={setAmount}
                  amountMin={methodMin}
                  amountMax={methodMax}
                  quickAmounts={quick}
                  usdtTry={method?.usdtTry}
                  amountError={tab === 'deposit' ? err : null}
                  amountErrorDetail={tab === 'deposit' ? errDetail : null}
                  submitLoading={loading}
                  onSubmitDeposit={() => {
                    if (loading || Number(amount) < methodMin || Number(amount) > methodMax || !cryptoAssetId) return;
                    submit({ preventDefault() {} });
                  }}
                  submitLabel={tr('account.payments.addAmountUsd', {
                    amount: Number(amount) >= methodMin
                      ? formatUsdWithTry(amount, method?.usdtTry)
                      : formatUsdWithTry(methodMin, method?.usdtTry),
                  })}
                />
              )}
              {showCryptoAmount ? (
                <>
                  <label className="mobile-account-cashier__field">
                    <span className="mobile-account-cashier__field-label">{tr('account.cashier.amount')}</span>
                    <input
                      className="mobile-account-cashier__input"
                      type="number"
                      min={methodMin}
                      max={methodMax || undefined}
                      step="1"
                      value={amount}
                      placeholder={tr('account.payments.from', { amount: methodMin })}
                      onChange={(event) => setAmount(event.target.value)}
                    />
                  </label>
                  {quick.length > 0 && (
                    <div className="mobile-account-cashier__quick">
                      {quick.map((item) => (
                        <button key={item} type="button" className="mobile-account-cashier__quick-btn" onClick={() => setAmount(String(item))}>
                          {item}
                        </button>
                      ))}
                    </div>
                  )}
                  {err && (
                    <div className="mobile-account-cashier__error">
                      <strong>{err}</strong>
                      {errDetail ? <span className="mobile-account-cashier__error-detail">{errDetail}</span> : null}
                    </div>
                  )}
                  <button
                    type="submit"
                    className={`mobile-account-cashier__submit${loading ? ' is-loading' : ''}`}
                    aria-busy={loading}
                    disabled={
                      loading
                      || Number(amount) < methodMin
                      || Number(amount) > methodMax
                      || (isCrypto && (!cryptoAssetId || (tab === 'withdraw' && !walletAddress)))
                    }
                  >
                    {loading
                      ? tr('account.loading')
                      : (tab === 'deposit'
                        ? tr('account.payments.addAmount', { amount: Number(amount) >= methodMin ? amount : methodMin })
                        : tr('account.payments.withdrawSubmit', { amount: Number(amount) >= methodMin ? amount : methodMin }))}
                  </button>
                </>
              ) : null}
              {isCrypto && tab === 'deposit' && err && (cryptoStep === 'amount' || cryptoStep === 'wallet') ? (
                <div className="mobile-account-cashier__error">
                  <strong>{err}</strong>
                  {errDetail ? <span className="mobile-account-cashier__error-detail">{errDetail}</span> : null}
                </div>
              ) : null}
            </form>
          )}
        </div>
      )}
    </div>
  );
}
